import { motion } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { HBLoadingSpinnerOnly } from "~components";
import { Css, Xss } from "~generated/css";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { useTestIds } from "~utils";
import {
  BASE_GEOMETRY,
  DIMENSIONS_FLIP,
  DIMENSIONS_STANDARD,
  FIRST_FLOOR_LABEL,
  FLOORPLAN_CAD,
  FLOORPLAN_FLIP,
  FLOORPLAN_STANDARD,
  gId,
  gIdCaseInsensitive,
  gIdContains,
  HIGHLIGHT,
  LOGO,
  OPT_CODE_PREFIX,
  OPTION_CODE_PREFIX,
  PLAN_TEXT,
  ROOMNAMES_FLIP,
  ROOMNAMES_STANDARD,
  SECOND_FLOOR_LABEL,
} from "./FloorPlan.constants";
import { FloorPlanOptionGroup_CheckoutOptionGroupFragment } from "./FloorPlan.generated";

// In-memory cache for SVG content to avoid re-fetching the same floor plan
const svgCache = new Map<string, Promise<string>>();

/**
 * Can be called early (e.g., when images are received) to preload and improve load times.
 * We do this because our SVGs are stored as assets, not raw text or images so we need to handle it ourselves so we're not waiting to fetch
 * them when we're in a model home with bad internet and not refetching them every time the floor plan is rendered.
 */
export function fetchAndCacheSVG(src: string): Promise<string> {
  if (!src) {
    return Promise.resolve("");
  }

  // Return cached promise if available
  const cached = svgCache.get(src);
  if (cached) {
    return cached;
  }

  // Create new fetch promise
  const fetchPromise = (async () => {
    try {
      const response = await fetch(src);
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.statusText}`);
      }
      return response.text();
    } catch (error) {
      // Remove failed cache entry so it can be retried
      svgCache.delete(src);
      throw error;
    }
  })();

  svgCache.set(src, fetchPromise);
  return fetchPromise;
}

type FloorPlanProps = {
  // URL to a .svg file representing the floor plan
  src: string;
  // Allow for styles to be applied to the container
  xss?: Xss<"height" | "width">;
  // Allow override of highlight color
  highlightHexColor?: string;
  // Allow override of mask color
  maskHexColor?: string;
  // Specific options to show geometry for. These are always highlighted when possible.
  selectedOptionCodes?: string[];
  // Whether to reverse the floor plan
  isReversed?: boolean;
  // Used to scope styles
  id: string;
  // Whether to reduce the amount of effective padding when shown in a small "preview" card
  isPreview?: boolean;
};

export const FloorPlan = (props: FloorPlanProps) => {
  const {
    src,
    xss,
    selectedOptionCodes = [],
    highlightHexColor = "#FFFF00",
    maskHexColor = "#FFFFFF",
    isReversed = false,
    isPreview = false,
  } = props;

  const tid = useTestIds(props, "floorPlan");

  // stores the SVG text that will be injected into the page via `dangerouslySetInnerHTML`
  const [svgText, setSvgText] = useState<string>("");
  // stores the reference to the `div` containing the `svg` element
  const svgContainerRef = useRef<HTMLDivElement>(null);
  // internal loading state
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Reset states when src changes
    setIsLoading(true);
    setSvgText("");

    // Fetch the .svg file content and inject into page via state.
    // We do this since using `<img />`, `<object />` or `<iframe />`
    // does not offer the ability to customize its styles
    void (async () => {
      try {
        // NOTE: This right here is why `Asset.downloadUrl` works. We're fetching it
        // explicitly here.
        const svgText = await fetchAndCacheSVG(src);
        setSvgText(svgText);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load floor plan SVG:", error);
        setIsLoading(false);
      }
    })();
  }, [src]);

  useLayoutEffect(() => {
    if (svgContainerRef.current && svgText && !isLoading) {
      const svg = svgContainerRef.current.querySelector("svg");
      if (svg) {
        try {
          resetFloorPlan(svg);
          hideMetaElements(svg);
          showOptions(svg, selectedOptionCodes, isReversed);
          highlightOptions(svg, selectedOptionCodes, highlightHexColor, isReversed);
        } catch (error) {
          console.error("Floor plan render error", error);
        }
      }
    }
  }, [highlightHexColor, isReversed, maskHexColor, selectedOptionCodes, svgText, isLoading]);

  return (
    <div css={Css.relative.h100.w100.$}>
      {/* Loading spinner mask */}
      <motion.div
        initial={{ opacity: 1 }}
        css={Css.absolute.bgOffWhite.z1.w100.h100.$}
        animate={
          !isLoading && {
            opacity: 0,
            transition: { duration: 0.4 },
            transitionEnd: { display: "none", zIndex: -1 },
          }
        }
      >
        <div css={Css.absolute.top("50%").left("50%").add("transform", "translate(-50%, -50%)").$}>
          <HBLoadingSpinnerOnly />
        </div>
      </motion.div>

      {/* Floor plan content */}
      <div
        css={{
          ...xss,
          svg: {
            height: "100%",
            width: "100%",
          },
        }}
        ref={svgContainerRef}
        dangerouslySetInnerHTML={{ __html: svgText }}
        {...tid}
      />
    </div>
  );
};

function resetFloorPlan(svg: SVGSVGElement) {
  const floorPlanFlip = svg.querySelector<SVGGElement>(gId(FLOORPLAN_FLIP));
  const dimensionsFlip = svg.querySelector<SVGGElement>(gId(DIMENSIONS_FLIP));
  const roomNamesFlip = svg.querySelector<SVGGElement>(gId(ROOMNAMES_FLIP));
  floorPlanFlip && (floorPlanFlip.style.display = "none");
  dimensionsFlip && (dimensionsFlip.style.display = "none");
  roomNamesFlip && (roomNamesFlip.style.display = "none");

  const allOptionLayers = Array.from(svg.querySelectorAll<SVGGElement>(gIdContains(OPT_CODE_PREFIX)));
  // Than this is a STRAND floor plan
  if (allOptionLayers.nonEmpty) {
    allOptionLayers.forEach((layer) => {
      layer.style.display = "none";
      const highlightGroups = Array.from(layer.querySelectorAll<SVGGElement>(gIdCaseInsensitive(HIGHLIGHT)));
      for (const highlightGroup of highlightGroups) {
        // Remove fill from all child elements within the highlight group
        const children = Array.from(highlightGroup.querySelectorAll<SVGElement>("*"));
        for (const child of children) {
          child.style.fill = "none";
          child.style.mixBlendMode = "none";
        }
      }
    });
  } else {
    // Then This is an internally created HB floor plan and we need to search for `OPTION-CODE-`
    const hbAllOptionLayers = Array.from(svg.querySelectorAll<SVGGElement>(gIdContains(OPTION_CODE_PREFIX)));
    hbAllOptionLayers.forEach((layer) => {
      layer.style.display = "none";
    });
  }
}

// Hide anything that's not the floor plan
function hideMetaElements(svg: SVGSVGElement) {
  const planText = svg.querySelectorAll<SVGGElement>(gIdContains(PLAN_TEXT))?.[0];
  const logo = svg.querySelectorAll<SVGGElement>(gIdContains(LOGO))?.[0];
  const firstFloorLabel = svg.querySelectorAll<SVGGElement>(gIdContains(FIRST_FLOOR_LABEL))?.[0];
  const secondFloorLabel = svg.querySelectorAll<SVGGElement>(gIdContains(SECOND_FLOOR_LABEL))?.[0];
  planText && (planText.style.display = "none");
  logo && (logo.style.display = "none");
  firstFloorLabel && (firstFloorLabel.style.display = "none");
  secondFloorLabel && (secondFloorLabel.style.display = "none");
}

export function isStrandPlanFp(svg: SVGSVGElement) {
  return (
    !!svg.querySelector<SVGGElement>(gIdContains(OPT_CODE_PREFIX)) ||
    !!svg.querySelector<SVGGElement>(gIdContains(FLOORPLAN_CAD))
  );
}

/**
 * Detects image-based floor plan SVGs (currently only Eaton Fire Community).
 * These SVGs wrap `<image>` tags inside BASE_GEOMETRY layers and default to all
 * base layers being hidden, which causes a blank render when no options are selected.
 */
export function isImageBasedFloorPlan(svg: SVGSVGElement): boolean {
  return svg.querySelectorAll<SVGGElement>(gIdContains(BASE_GEOMETRY)).length > 0;
}

export function expectLayerAndTextFlip(svg: SVGSVGElement) {
  const floorPlanStandard = svg.querySelector<SVGGElement>(gId(FLOORPLAN_STANDARD));
  const floorPlanFlip = svg.querySelector<SVGGElement>(gId(FLOORPLAN_FLIP));
  return !!floorPlanStandard && !floorPlanFlip;
}

function showOptions(svg: SVGSVGElement, selectedOptionCodes: string[], isReversed: boolean) {
  // or isDallasInfillPlan
  const isStrandPlan = isStrandPlanFp(svg);

  if (isStrandPlan) {
    // If we dont have individual layers for the standard and the flip we need to flip the entire floor plan and then
    // update the individual layers
    const needsLayerAndTextFlip = expectLayerAndTextFlip(svg);
    if (needsLayerAndTextFlip) {
      setFloorPlanDirection(svg, isReversed);
      for (const selectedOptionCode of selectedOptionCodes) {
        const optionCodeId = `${OPT_CODE_PREFIX}${selectedOptionCode}`;
        const optionLayer = svg.querySelector<SVGGElement>(gId(optionCodeId));
        if (optionLayer) {
          optionLayer.style.display = "inline";
        }
      }
    } else {
      // Lakeside strand plans require us to TOGGLE layers
      // whereas the earlier strand plans (Dallas Infill) layered the flips on top of standard so it wasnt required
      // We have communicated this with design in hopes of updating older plans and then going forward
      // we will always toggle.
      const floorPlanStandard = svg.querySelector<SVGGElement>(gId(FLOORPLAN_STANDARD));
      const floorPlanFlip = svg.querySelector<SVGGElement>(gId(FLOORPLAN_FLIP));
      const dimensionsStandard = svg.querySelector<SVGGElement>(gId(DIMENSIONS_STANDARD));
      const dimensionsFlip = svg.querySelector<SVGGElement>(gId(DIMENSIONS_FLIP));
      const roomNamesStandard = svg.querySelector<SVGGElement>(gId(ROOMNAMES_STANDARD));
      const roomNamesFlip = svg.querySelector<SVGGElement>(gId(ROOMNAMES_FLIP));
      floorPlanFlip && (floorPlanFlip.style.display = isReversed ? "inline" : "none");
      floorPlanStandard && (floorPlanStandard.style.display = isReversed ? "none" : "inline");
      dimensionsFlip && (dimensionsFlip.style.display = isReversed ? "inline" : "none");
      roomNamesFlip && (roomNamesFlip.style.display = isReversed ? "inline" : "none");
      dimensionsStandard && (dimensionsStandard.style.display = isReversed ? "none" : "inline");
      roomNamesStandard && (roomNamesStandard.style.display = isReversed ? "none" : "inline");
      for (const selectedOptionCode of selectedOptionCodes) {
        const optionCodeId = `${OPT_CODE_PREFIX}${selectedOptionCode}`;
        const optionCodeFlipId = `${OPT_CODE_PREFIX}${selectedOptionCode}_FLIP`;
        const optionLayer = svg.querySelector<SVGGElement>(gId(optionCodeId));
        const optionLayerFlip = svg.querySelector<SVGGElement>(gId(optionCodeFlipId));
        if (optionLayer) {
          optionLayer.style.display = !isReversed ? "inline" : "none";
          optionLayerFlip && (optionLayerFlip.style.display = isReversed ? "inline" : "none");
        }
      }
    }
  } else {
    // Eaton Fire community
    // These HB created floor plans use `OPTION-CODE-` instead of `OPT_CODE-`, dont have flips, and dont have any text
    // to manipulate.
    // They're very incomplete and a rush job we'll need to iterate on as they get updated until they're replaced by Strand plans.
    // we do the same as above but without readable flips and text manipulation.
    svg.style.transform = isReversed ? "scaleX(-1)" : "scaleX(1)";

    // Image-based SVGs (Eaton Fire) have all BASE_GEOMETRY layers hidden by default.
    // When no options are selected, show the first base layer so the plan isn't blank.
    if (selectedOptionCodes.isEmpty && isImageBasedFloorPlan(svg)) {
      const firstBaseLayer = svg.querySelector<SVGGElement>(gIdContains(BASE_GEOMETRY));
      if (firstBaseLayer) firstBaseLayer.style.display = "inline";
    }

    for (const selectedOptionCode of selectedOptionCodes) {
      const optionCodeId = `${OPTION_CODE_PREFIX}${selectedOptionCode}`;
      // Note, we're using a wildcard to match the option code id since we're rushing and not going to manually update ids
      const optionLayer = svg.querySelector<SVGGElement>(gIdContains(optionCodeId));
      if (optionLayer) {
        const isHidden = optionLayer.style.display === "none";
        optionLayer.style.display = isHidden ? "inline" : "none";
      }
    }
  }
}

// flip the SVG and elements within it horizontally
function setFloorPlanDirection(svg: SVGSVGElement, isReversed: boolean) {
  const reversableElements = getReversableElements(svg);
  if (isReversed) {
    svg.style.transform = "scaleX(-1)";
    reversableElements.forEach((group) => {
      group.style.transform = "rotateY(180deg)";
      group.style.transformOrigin = "50%";
      group.style.transformBox = "fill-box";
    });
  } else {
    svg.style.transform = "scaleX(1)";
    reversableElements.forEach((group) => {
      group.style.transform = "rotateY(0deg)";
      group.style.transformOrigin = "0";
      group.style.transformBox = "view-box";
    });
  }
}

function getReversableElements(svg: SVGSVGElement) {
  // correct the text - find all the `<g />` elements with the proper ids
  const textGroups = [
    ...Array.from(svg.querySelectorAll<SVGGElement>(gIdContains(DIMENSIONS_STANDARD))),
    ...Array.from(svg.querySelectorAll<SVGGElement>(gIdContains(ROOMNAMES_STANDARD))),
    // Also search inside option code layers for text that needs counter-flipping
    // just in case its not where it should be.
    ...Array.from(svg.querySelectorAll<SVGGElement>(gIdContains(OPT_CODE_PREFIX))),
  ].flatMap((group) => Array.from(group.querySelectorAll<SVGElement>(gIdContains("TEXT"))));
  return textGroups;
}

function highlightOptions(
  svg: SVGSVGElement,
  highlightedOptionCodes: string[],
  highlightHexColor: string,
  isReversed: boolean,
) {
  // Apply the highlight fill to the selected option codes' highlight layers
  for (const optionCode of highlightedOptionCodes) {
    const optionLayer = svg.querySelector<SVGGElement>(gId(`${OPT_CODE_PREFIX}${optionCode}`));
    const optionLayerFlip = svg.querySelector<SVGGElement>(gId(`${OPT_CODE_PREFIX}${optionCode}_FLIP`));

    // NOTE: Been seeing inconsistent results using expectLayerAndTextFlip here in storybook
    // so we'll just check for a flip layer directly. Maybe we're manipulating the SVG too much?
    const activeLayer = isReversed && optionLayerFlip ? optionLayerFlip : optionLayer;
    if (!activeLayer) continue;

    const highlightGroups = Array.from(activeLayer.querySelectorAll<SVGGElement>(gIdContains(HIGHLIGHT)));
    for (const highlightGroup of highlightGroups) {
      // should only be one rect
      const children = Array.from(highlightGroup.querySelectorAll<SVGElement>("*"));
      for (const child of children) {
        child.style.mixBlendMode = "multiply";
        child.style.fill = highlightHexColor;
      }
    }
  }
}

export function getSelectedStructuralOptionCodes(
  optionGroups: FloorPlanOptionGroup_CheckoutOptionGroupFragment[],
  selectedOptionIds: string[],
) {
  return optionGroups
    .filter(
      (ccgo) =>
        ccgo.step === CheckoutConfigOptionGroupStep.OptionsStructural ||
        // LA Eaton fire plan elevations contribute to the floor plan
        ccgo.step === CheckoutConfigOptionGroupStep.ExteriorElevation,
    )
    .flatMap((ccog) => ccog.options)
    .filter((cco) => selectedOptionIds.includes(cco.id))
    .map((option) => option.option.code);
}

// ADU has all options on the same step so we give it its own util so we don't accidentally turn on
// options that shouldn't be. How could that happen?
// A structural option that is applied to both the ADU and main building may come from the same global option
// where they would have the same option code.
export function getSelectedAduOptionCodes(
  optionGroups: FloorPlanOptionGroup_CheckoutOptionGroupFragment[],
  selectedOptionIds: string[],
) {
  return optionGroups
    .filter((ccgo) => ccgo.step === CheckoutConfigOptionGroupStep.Adu)
    .flatMap((ccog) => ccog.options)
    .filter((cco) => selectedOptionIds.includes(cco.id))
    .map((option) => option.option.code);
}
